import { Alert, Badge, Button, Card, Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import useComment from '../../../hooks/cms/useComment';
import React, { useEffect, useState } from 'react';
import { CommentDetails, CommentNew } from '../../../types/cms/comment';
import { convertToHumanDate } from '../../../utils';
import { Language, Languages } from '../../../layouts/types';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../../hooks';
import { UserRoleType } from '../../../types/user/role';
import Spinner from '../../atoms/Spinner/Spinner';
import { AdsContainer, AdType } from '../../atoms/AdsContainer/AdsContainer';

interface CommentsCardProps {
    domain: string;
    domainId: string;
}

const CommentsCard = ({ domain, domainId }: CommentsCardProps) => {
    const [currentLang, setCurrentLang] = useState<Language | null>(null);
    const [taggedComment, setTaggedComment] = useState<string | null>(null);
    const [comment, setComment] = useState<string>('');
    const { i18n, t } = useTranslation();
    const { loggedInUserRoles } = useUser();

    useEffect(() => {
        const currentLang = Languages.find((lang) => lang.code === i18n.language) ?? Languages[0];
        setCurrentLang(currentLang);
    }, [i18n.language]);

    const { loading, userLoadComments, userViewComments, userSendComment, deleteSuccess, adminDeleteComment, apiSuccess, sendSuccess } = useComment();

    const loadComments = () => {
        userLoadComments(domain, domainId);
    };

    useEffect(() => {
        loadComments();
    }, []);

    useEffect(() => {
        if (sendSuccess) {
            userLoadComments(domain, domainId);
        }
    }, [sendSuccess]);

    useEffect(() => {
        if (deleteSuccess) {
            userLoadComments(domain, domainId);
        }
    }, [deleteSuccess]);

    const deleteComment = (commentId: string) => {
        adminDeleteComment(domain, commentId);
    };

    const processComment = () => {
        if (comment.trim().length > 0) {
            const commentData: CommentNew = {
                content: comment,
                parentId: taggedComment ? taggedComment : undefined,
                moduleId: domain === 'modules' ? domainId : undefined,
                lectureId: domain === 'lectures' ? domainId : undefined,
                problemId: domain === 'problems' ? domainId : undefined,
            };
            userSendComment(commentData);
            setComment('');
            setTaggedComment(null);
        }
    };
    return (
        <>
            {userViewComments && currentLang && (
                <Card>
                    <Card.Header className='py-2 bg-primary'>
                        <h4 className='header-title mb-0 text-white fw-bolder'>
                            {t('comment.plural')} - {userViewComments.length}
                        </h4>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col>
                                {sendSuccess && (
                                    <Alert variant='success' className='my-2'>
                                        {t('comment.message_success')}
                                    </Alert>
                                )}
                                <div className='border rounded'>
                                    <form className='comment-area-box'>
                                        <div className='p-2 d-flex bg-light justify-content-center align-items-center'>
                                            <textarea
                                                rows={3}
                                                className='form-control border-0 resize-none'
                                                placeholder={t('comment.message_placeholder')}
                                                value={comment}
                                                onChange={(e) => setComment(e.target.value)}
                                            />
                                        </div>
                                        <div className='p-2 d-flex justify-content-end align-items-center'>
                                            <Button onClick={processComment} disabled={loading}>
                                                <i className='uil uil-message me-1'></i>
                                                {t('comment.message_button')}
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                                {userViewComments.map((comment: CommentDetails, index: number) => {
                                    return (
                                        <div className={classNames('d-flex', 'border shadow p-2 mt-2')} key={index.toString()}>
                                            <Link to={`/profile/${comment.user.username}`}>
                                                <img className='rounded-circle avatar-sm me-2' src={comment.user.avatarUrl} alt='' />
                                            </Link>
                                            <div className='w-100'>
                                                <h5 className='mt-0 mb-1'>
                                                    <Link to={`/profile/${comment.user.username}`} className='fw-bolder'>
                                                        {comment.user.displayName}
                                                    </Link>
                                                    {comment.user.roles && comment.user.roles.includes(UserRoleType.ADMIN) && (
                                                        <Badge className='ms-1' bg='danger'>
                                                            Admin
                                                        </Badge>
                                                    )}
                                                    {comment.user.roles && comment.user.roles.includes(UserRoleType.SUBSCRIBER) && (
                                                        <Badge className='ms-1' bg='primary'>
                                                            Premium
                                                        </Badge>
                                                    )}
                                                    <br />
                                                    <small className='text-muted'>{convertToHumanDate(comment.updatedAt, currentLang.code)}</small>
                                                </h5>
                                                <div className='mb-1'>
                                                    <span style={{ whiteSpace: 'break-spaces' }}>{comment.content}</span>
                                                </div>
                                                <Button
                                                    className='btn-xs'
                                                    onClick={() => {
                                                        setTaggedComment(comment.id);
                                                    }}
                                                    disabled={taggedComment === comment.id}>
                                                    @
                                                </Button>
                                                <Button className='btn-xs ms-1' variant='danger'>
                                                    !
                                                </Button>
                                                {loggedInUserRoles.includes(UserRoleType.ADMIN) && (
                                                    <Button className='btn-xs ms-1 btn-strongred float-end' variant='danger' onClick={() => deleteComment(comment.id)}>
                                                        <i className='mdi mdi-trash-can'></i>
                                                    </Button>
                                                )}
                                                {comment.children.map((reply, indexChildren) => {
                                                    return (
                                                        <div className='d-flex mt-3' key={indexChildren.toString()}>
                                                            <Link to={`/profile/${reply.user.username}`}>
                                                                <img src={reply.user.avatarUrl} className='rounded-circle avatar-sm me-3' alt='' height='32' />
                                                            </Link>
                                                            <div className='w-100'>
                                                                <h5 className='mt-0 mb-1'>
                                                                    <Link to={`/profile/${reply.user.username}`} className='fw-bolder'>
                                                                        {reply.user.displayName}
                                                                    </Link>
                                                                    {reply.user.roles && reply.user.roles.includes(UserRoleType.ADMIN) && (
                                                                        <Badge className='ms-1' bg='danger'>
                                                                            Admin
                                                                        </Badge>
                                                                    )}
                                                                    {reply.user.roles && reply.user.roles.includes(UserRoleType.SUBSCRIBER) && (
                                                                        <Badge className='ms-1' bg='primary'>
                                                                            Premium
                                                                        </Badge>
                                                                    )}
                                                                    <br />
                                                                    <small className='text-muted'>{convertToHumanDate(reply.updatedAt, currentLang.code)}</small>
                                                                </h5>
                                                                <div className='mb-1'>
                                                                    <span style={{ whiteSpace: 'break-spaces' }}>{reply.content}</span>
                                                                </div>
                                                                <Button className='btn-xs' variant='danger'>
                                                                    !
                                                                </Button>
                                                                {loggedInUserRoles.includes(UserRoleType.ADMIN) && (
                                                                    <Button className='btn-xs ms-1 btn-strongred float-end' variant='danger' onClick={() => deleteComment(reply.id)}>
                                                                        <i className='mdi mdi-trash-can'></i>
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}
            {!userViewComments && (
                <Card>
                    <Card.Header className='py-2 bg-primary'>
                        <h4 className='header-title mb-0 text-white fw-bolder'>{t('comment.plural')}</h4>
                    </Card.Header>
                    <Card.Body>
                        <Row className='justify-content-center'>
                            <Spinner className='m-2' color='primary' size={'sm'} />
                        </Row>
                    </Card.Body>
                </Card>
            )}
        </>
    );
};

export default CommentsCard;
